<template>
  <UeModal
    ref="csvImportModal"
    name="csv_import_modal"
    width="95%"
    height="95%"
    :maxWidth="1240"
    :adaptive="true"
    :title="modalParams.title || 'Import CSV'"
    scrollable
    @before-open="resetState"
  >
    <div v-if="errors.length > 0" class="alert alert-warning" role="alert">
      <div v-for="error in errors" :key="error">{{ error }}</div>
    </div>
    <!-- Step 1: File Selection -->
    <div v-if="currentStep === 1" class="file-upload-step">
      <div class="file-input-container border rounded p-4 text-center">
        <input
          type="file"
          accept=".csv"
          @change="handleFileChange"
          class="d-none"
          ref="fileInput"
        />
        <div class="mb-3">
          <button type="button" class="btn btn-primary" @click="$refs.fileInput.click()">
            Select CSV File
          </button>
        </div>
        <div v-if="csvFile" class="mt-3">Selected file: {{ csvFile.name }}</div>
      </div>
    </div>

    <!-- Step 2: Field Mapping -->
    <div v-if="currentStep === 2">
      <div class="mb-4">
        <h5 class="mb-3">Map CSV Fields to Destination Fields</h5>
        <div class="text-muted mb-3">
          Match the columns from your CSV file to the corresponding fields in the system.
        </div>
      </div>
      <table class="table">
        <thead>
          <tr>
            <th style="width: 40%">CSV Column</th>
            <th style="width: 60%">Map to Field</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(header, i) in incomingFields" :key="i">
            <td>
              <div class="font-weight-bold">{{ header.name }}</div>
              <div class="text-muted small" v-if="header.preview">
                Preview: {{ header.preview }}
              </div>
            </td>
            <td>
              <UeSelect
                :options="selectOptions"
                v-model="header.mapping"
                labelField="label"
                keyField="key"
                placeholder="Select field..."
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <template #footer>
      <div class="w-100 d-flex justify-content-between align-items-center">
        <div>
          <!-- Don't remove the above wrapper <div> -->
          <button
            v-if="currentStep === 2"
            type="button"
            class="btn btn-link"
            @click="currentStep = 1"
          >
            ← Back to File Selection
          </button>
        </div>
        <div class="d-flex">
          <button type="button" class="btn btn-secondary mr-2" @click="closeModal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            v-if="currentStep === 2"
            :disabled="!canProceed"
            @click="handleStepAction"
          >
            Import
          </button>
        </div>
      </div>
    </template>
  </UeModal>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Papa from 'papaparse'
import { useModalStore } from '@/stores/ModalStore'

const MODAL_NAME = 'csv_import_modal'

interface Constraint {
  type: string
}

interface FieldOpts {
  separator?: string
}

interface Field {
  key: string
  type: string
  label: string
  constraints?: Constraint[]
  options?: FieldOpts
}

interface IncomingField {
  name: string
  index: number
  mapping: Field | null
  preview?: string
}

interface ParseResult {
  data: string[][]
  errors: { message: string }[]
}

interface ModalParams {
  title: string
  mapFields: Field[]
  meta: Record<string, unknown>
  importEndpoint: (payload: Record<string, unknown>) => Promise<void>
}

export default defineComponent({
  name: 'ImportModal',
  data() {
    return {
      // Determine which step to show
      currentStep: 1,
      // Set when the CSV file is selected
      selectedCsvFile: null,
      // Set when the CSV file is parsed
      parsedRows: [] as string[][],
      // Set when the the CSV file is parsed, and headers are extracted
      incomingFields: [] as IncomingField[],
      // Holds an array of errors
      errors: [] as string[],
    }
  },
  computed: {
    modalParams(): ModalParams {
      return (useModalStore().getModalParams(MODAL_NAME) || {}) as ModalParams
    },
    mapFields(): Field[] {
      return this.modalParams.mapFields
    },
    csvFile(): File | null {
      // This allows us to pass a file to the modal
      // Useful for setting a state in Storybook.
      return this.modalParams.csvFile || this.selectedCsvFile
    },
    canProceed(): boolean {
      if (this.errors.length > 0) {
        return false
      }

      if (this.currentStep === 1) {
        return !!this.csvFile
      } else {
        return this.incomingFields.some((field: IncomingField) => field.mapping !== null)
      }
    },
    selectOptions(): { value: string; label: string }[] {
      return (
        this.mapFields?.map((field: Field) => ({
          key: field.key,
          label: field.label,
          options: field.options,
        })) ?? []
      )
    },
  },
  watch: {
    csvFile(newFile) {
      if (newFile) {
        this.parseCsvFile()
      }
    },
  },
  methods: {
    clearErrors() {
      this.errors = []
    },
    handleFileChange(event: Event) {
      this.clearErrors()
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]

      if (!file && !file.name.toLowerCase().endsWith('.csv')) {
        return
      }

      this.selectedCsvFile = file
    },
    parseCsvFile() {
      Papa.parse(this.csvFile, {
        complete: (results: ParseResult) => {
          if (results.data.length < 2) {
            this.errors.push('The CSV file appears to be empty')
            return
          }

          const headers = results.data[0]
          const previewRow = results.data[1]

          this.parsedRows = results.data
          this.incomingFields = headers
            ? headers.map((name: string, index: number) => ({
                name,
                index,
                mapping: null as string | null,
                preview: previewRow[index],
              }))
            : []
          this.currentStep = 2
        },
        error: (error: { message: string }) => {
          this.errors.push(`Error reading CSV: ${error.message}`)
        },
      })
    },
    handleStepAction() {
      if (this.currentStep === 1 && this.csvFile) {
        this.currentStep = 2
      } else if (this.currentStep === 2) {
        this.submitMapping()
      }
    },
    submitMapping() {
      const mapping = this.incomingFields.map((field: IncomingField) => {
        return {
          i: field.index,
          mapping: field.mapping,
        }
      })
      const payload = {
        csv_import: {
          rows: JSON.stringify(this.parsedRows.slice(1)),
          mapping: JSON.stringify(mapping),
          headers: JSON.stringify(this.parsedRows[0]),
          csv_file: this.csvFile,
          meta: this.modalParams.meta,
        },
      }

      if (this.modalParams.importEndpoint) {
        this.modalParams.importEndpoint(payload).then(() => {
          this.closeModal()
        })
      }
    },
    resetState() {
      this.currentStep = 1
      this.selectedCsvFile = null
      this.incomingFields = []
      this.parsedRows = []
      this.errors = []

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    closeModal() {
      this.$closeModal(MODAL_NAME)
    },
  },
})
</script>

<style lang="sass" scoped>
.file-input-container
  min-height: 200px
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center
</style>
